import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryCategory, Item, StoreModel, PrimaryCategoryModel, CreateCountZoneRequest, ItemModel, UpdateCountZoneRequest, ItemsWithCategoryId, GetItemsForCategoriesRequest, CountZoneTypeResponse } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import useLogError from '../../../hooks/useLogError';

export const useStockCountZone = () => {

  //use hooks
  const { t } = useTranslation('stockCountZonesWithItems');
  const { getCountZoneDetails, countZonePost, countZoneUpdate, getItemsFromCategories, getCountZoneTypes } = useNswagClient();
  const { logError } = useLogError();
  const navigate = useNavigate();

  const [stores, setStores] = useState<StoreModel[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const firstSelectedStore = stores.find(s => s.storeNumber === selectedStores[0]);
  const { user, hasPermissionTo } = useContext(UserContext);

  //modal open state
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] = useState(false);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isItemsDeleteModalOpen, setIsItemsDeleteModalOpen] = useState(false);

  //row delete ids
  const [categoryIdToDelete, setCategoryIdToDelete] = useState<number>();
  const [itemNumberToDelete, setItemNumberToDelete] = useState<string>();

  //other state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryData, setCategoryData] = useState<PrimaryCategory[]>([]);
  const [itemsData, setItemsData] = useState<ItemModel[]>([]);
  const [hasCreated, setHasCreated] = useState(false);

  //main box values
  const [zoneName, setZoneName] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const { id } = useParams();
  const isCreating = Number(id) <= 0 || id === undefined;
  const blocker = useBlocker(() => hasChanged && !hasCreated);

  //count zone types
  const [countZoneTypes, setCountZoneTypes] = useState<CountZoneTypeResponse[]>([]);
  const [selectedCountZoneType, setSelectedCountZoneType] = useState<number | undefined>(undefined);

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  const [hasUserReorderedItems, setHasUserReorderedItems] = useState(false);

  useEffect(() => {
    if (hasPermissionTo(['StockLocationRead'])) {
      loadData();
    }
    setStores(user?.stores ?? []);
    if (!id) {
      return;
    }
  }, []);

  useEffect(() => {
    if (selectedStores.length === 0) {
      setCategoryData([]);
      setItemsData([]);
      setCountZoneTypes([]);
      setSelectedCountZoneType(undefined);
    } else if (selectedStores[0]) {
      fetchCountZoneTypes(selectedStores[0]);
    }
  }, [selectedStores[0]]);

  const fetchCountZoneTypes = (storeNumber: string) => {
    if (!storeNumber) {
      return;
    }
    
    getCountZoneTypes(storeNumber)
      .then((types) => {
        setCountZoneTypes(types || []);
      })
      .catch((e) => {
        setCountZoneTypes([]);
        logError(e);
      });
  };

  const loadData = () => {
    setIsLoading(true);
    getCountZoneDetails(Number(id ?? 0)).then((result) => {
      if (result.data) {

        const CountZoneDetails = result.data;

        const storeNumbers = CountZoneDetails.stores
          ?.map(s => s.storeNumber)
          .filter(Boolean) as string[];
        setSelectedStores(storeNumbers);
        setZoneName(CountZoneDetails.locationName ?? '');
        setCategoryData(CountZoneDetails.primaryCategories ?? []);
        setItemsData(CountZoneDetails.items ?? []);
        setSelectedCountZoneType(CountZoneDetails.countZoneTypeId);
      }
    })
      .catch(e => {
        logError(e);
        setSnackBarProps('error', `Error loading stock count zone details: ${e}. please try again`);
      })
      .finally(() => setIsLoading(false));
  };

  const handleAddCategoriesClick = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleCategories = async (data: PrimaryCategoryModel[]) => {
    const convertedCategories = data.map(x => ({
      id: x.id,
      primaryCategoryName: x.primaryCategoryName,
    }));

    const categories = Array.from(new Set(categoryData.concat(convertedCategories)));
    setCategoryData(categories);
    setIsCategoriesModalOpen(false);
    setHasChanged(true);

    // Fetch items for the newly added categories and selected stores
    const categoryIds = convertedCategories.map(c => c.id as number);
    if (categoryIds.length > 0 && selectedStores.length > 0) {
      try {
        const body: GetItemsForCategoriesRequest = {
          categoryId: categoryIds,
          storeNumber: selectedStores,
        };
        const items: ItemsWithCategoryId[] = await getItemsFromCategories(body);

        const newItems: ItemModel[] = items
          .filter(item => item.itemNumber && !itemsData.some(x => x.itemNumber === item.itemNumber))
          .map(item => ({
            itemNumber: item.itemNumber,
            itemDescription: item.description,
            primaryCategoryId: item.primaryCategoryId,
            primaryCategory: item.primaryCategory,
            rowNumber: undefined,
          }));

        let mergedItems: ItemModel[];
        if (hasUserReorderedItems) {
          // Only sort new items, append to end
          newItems.sort((a, b) => (a.itemDescription ?? '').localeCompare(b.itemDescription ?? ''));
          mergedItems = [...itemsData, ...newItems];
        } else {
          // Sort all items alphabetically
          mergedItems = [...itemsData, ...newItems].sort((a, b) =>
            (a.itemDescription ?? '').localeCompare(b.itemDescription ?? ''),
          );
        }
        mergedItems = mergedItems.map((item, idx) => ({
          ...item,
          rowNumber: idx + 1,
        }));

        setItemsData(mergedItems);
      } catch (e) {
        logError(e);
      }
    }
  };

  const deleteCategoryRow = (idToDelete?: number) => {
    setCategoryIdToDelete(idToDelete);
    setIsCategoryDeleteModalOpen(true);
  };

  const deleteCategories = (data: PrimaryCategoryModel[], deletedCategoryIds: number[]) => {
    setCategoryData(data);

    let updatedItems = itemsData.filter(
      item => !deletedCategoryIds.includes(item.primaryCategoryId ?? -1),
    );

    if (!hasUserReorderedItems) {
      updatedItems = updatedItems.sort((a, b) =>
        (a.itemDescription ?? '').localeCompare(b.itemDescription ?? ''),
      );
    }

    updatedItems = updatedItems.map((item, idx) => ({
      ...item,
      rowNumber: idx + 1,
    }));

    setItemsData(updatedItems);

    handleClose();
    setHasChanged(true);
  };

  const handleClickOpen = () => {
    setIsCategoriesModalOpen(true);
    setIsItemsDeleteModalOpen(true);
  };

  const handleClose = () => {
    setIsCategoriesModalOpen(false);
    setIsCategoryDeleteModalOpen(false);
    setIsItemsModalOpen(false);
    setIsBulkModalOpen(false);
    setIsItemsDeleteModalOpen(false);
  };

  const handleAddItemsClick = () => {
    setIsItemsModalOpen(true);
  };

  const handleAddBulkItemClick = () => {
    setIsBulkModalOpen(true);
  };

  const deleteItems = () => {
    let data: ItemModel[] = [];
    if (itemNumberToDelete != undefined) {
      data = itemsData?.filter(item => item.itemNumber !== itemNumberToDelete);
    }
    setItemsData(data);
    handleClose();
    setHasChanged(true);
  };

  const deleteItemRow = (itemNumberToDelete?: string) => {
    setItemNumberToDelete(itemNumberToDelete);
    setIsItemsDeleteModalOpen(true);
  };

  const handleItems = (data: Item[], removedItemNumbers: string[] = []) => {
    const baseItems = removedItemNumbers.length
      ? itemsData.filter(x => !removedItemNumbers.includes(x.itemNumber ?? ''))
      : itemsData;

    const convertedData: ItemModel[] = data
      .filter(item => !baseItems.some(x => x.itemNumber === item.itemNumber))
      .map(item => ({
        itemNumber: item.itemNumber,
        itemDescription: item.description,
        primaryCategory: item.primaryCategory,
        zoneNames: item.zoneNames,
        rowNumber: undefined,
      }));

    let combined: ItemModel[];
    if (hasUserReorderedItems) {
      // Only sort new items, append to end
      convertedData.sort((a, b) => (a.itemDescription ?? '').localeCompare(b.itemDescription ?? ''));
      combined = [...baseItems, ...convertedData];
    } else {
      // Sort all items alphabetically
      combined = [...baseItems, ...convertedData].sort((a, b) =>
        (a.itemDescription ?? '').localeCompare(b.itemDescription ?? ''),
      );
    }

    const uniqueItems = Array.from(
      new Map(combined.map(item => [item.itemNumber, item])).values(),
    ).map((item, idx) => ({
      ...item,
      rowNumber: idx + 1,
    }));

    setItemsData(uniqueItems);
    setHasChanged(true);
  };

  const submitCountZone = () => {
    isCreating ? createCountZone() : updateCountZone();
  };

  const createCountZone = () => {
    const body: CreateCountZoneRequest = {
      name: zoneName,
      storeNumbers: selectedStores,
      categoryIds: categoryData.map(c => c.id as number),
      items: itemsData.map((item, index) => ({
        itemNumber: item.itemNumber,
        itemDescription: item.itemDescription,
        primaryCategoryId: item.primaryCategoryId,
        primaryCategory: item.primaryCategory,
        rowNumber: index + 1,
      })),
      ...(selectedCountZoneType && { countZoneTypeId: selectedCountZoneType }),
    };

    setSnackBarProps('info', 'Creating count zone');
    setHasCreated(true);
    setIsSubmitting(true);

    countZonePost(body)
      .then(() => {
        setSnackBarProps('success', 'Created count zone');
        navigate('/stock-count-schedule/3');
      })
      .catch((error) => {
        const message = error?.message || JSON.stringify(error);
        setSnackBarProps('error', t('errorMessage') + ': ' + message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const updateCountZone = () => {
    if (!id) {
      setSnackBarProps('error', 'Invalid zone ID for update.');
      return;
    }

    const body: UpdateCountZoneRequest = {
      stockZoneId: Number(id),
      name: zoneName,
      storeNumbers: selectedStores,
      categoryIds: categoryData.map(c => c.id as number),
      items: itemsData.map((item, index) => ({
        itemNumber: item.itemNumber,
        itemDescription: item.itemDescription,
        primaryCategoryId: item.primaryCategoryId,
        primaryCategory: item.primaryCategory,
        rowNumber: index + 1,
      })),
      ...(selectedCountZoneType && { countZoneTypeId: selectedCountZoneType }),
    };

    setSnackBarProps('info', 'Updating count zone');
    setIsSubmitting(true);

    countZoneUpdate(body)
      .then(() => {
        setSnackBarProps('success', 'Updated count zone');
        setHasChanged(false);
        setHasUserReorderedItems(false);
        loadData();
      })
      .catch((error) => {
        const message = error?.message || JSON.stringify(error);
        setSnackBarProps('error', t('errorMessage') + ': ' + message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const reorderItems = (newItems: ItemModel[]) => {
    setItemsData(newItems);
    setHasChanged(true);
    setHasUserReorderedItems(true);
  };

  const handleLeaveClick = () => {
    navigate('/stock-count-schedule/3');
  };

  const handleLeavePageModalClose = () => {
    blocker?.proceed?.();
  };

  const handleLeavePageModalConfirm = () => {
    submitCountZone();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  return {
    t,
    zoneName,
    setZoneName,
    isLoading,
    isSubmitting,
    categoryData,
    itemsData,
    submitCountZone,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    stores,
    selectedStores,
    setSelectedStores: (stores: string[]) => {
      setSelectedStores(stores);
      if (stores.length === 0) {
        setCategoryData([]);
        setItemsData([]);
      }
    },
    firstSelectedStore,
    handleAddCategoriesClick,
    handleCategories,
    isCategoriesModalOpen,
    handleClose,
    deleteCategoryRow,
    deleteCategories,
    handleClickOpen,
    isCategoryDeleteModalOpen,
    handleAddItemsClick,
    handleAddBulkItemClick,
    deleteItems,
    deleteItemRow,
    isItemsModalOpen,
    handleItems,
    handleLeaveClick,
    reorderItems,
    isItemsDeleteModalOpen,
    isBulkModalOpen,
    isCreating,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    blocker,
    categoryIdToDelete,
    hasUserReorderedItems,
    countZoneTypes,
    selectedCountZoneType,
    setSelectedCountZoneType,
  };
};
